import React, { useEffect, useState } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { useAuth } from '../context/AuthContext'
import PlayerSetup from '../components/PlayerSetup'
import PreRaceDashboard from '../components/PreRaceDashboard'
import RaceDayDashboard from '../components/RaceDayDashboard'
import Results from '../components/Results'
import PlayerPage from '../components/PlayerPage'
import BorgWarnerBackground from '../components/BorgWarnerBackground'

interface Assignment { playerName: string; drivers: { number: number; name: string; team?: string }[] }
interface Prizes { first: number; second: number; third: number }
type Screen = 'loading' | 'setup' | 'prerace' | 'race' | 'results' | 'player'

export default function GameView() {
  const { slug, playerIndex } = useParams<{ slug: string; playerIndex?: string }>()
  const { user, token } = useAuth()
  const navigate = useNavigate()

  const [screen,      setScreen]      = useState<Screen>('loading')
  const [assignments, setAssignments] = useState<Assignment[]>([])
  const [prizes,      setPrizes]      = useState<Prizes>({ first: 50, second: 30, third: 20 })
  const [gameStatus,  setGameStatus]  = useState('setup')
  const [isCreator,   setIsCreator]   = useState(false)
  const [raceStarted, setRaceStarted] = useState(false)
  const [selectedPlayer, setSelectedPlayer] = useState(0)
  const [finalLeaderboard, setFinalLeaderboard] = useState<any[]>([])
  const [error, setError] = useState('')

  // Load game data
  useEffect(() => {
    if (!slug) return
    fetch(`/api/games/${slug}`)
      .then(r => r.json())
      .then(data => {
        if (!data.game) { setError('Game not found'); setScreen('setup'); return }
        const g = data.game
        setAssignments(g.assignments ?? [])
        setPrizes(g.prizes ?? { first: 50, second: 30, third: 20 })
        setGameStatus(g.status)

        // Check if current user is creator
        // (We'll verify server-side; this is just for UI hints)
        const creatorHint = token !== null
        setIsCreator(creatorHint)

        // Determine which screen to show
        if (g.status === 'finished') {
          setScreen('results')
        } else if (g.status === 'live') {
          setRaceStarted(true)
          // If arrived via player-specific link
          if (playerIndex !== undefined) {
            setSelectedPlayer(parseInt(playerIndex))
            setScreen('player')
          } else {
            setScreen('race')
          }
        } else if (g.assignments?.length > 0) {
          if (playerIndex !== undefined) {
            setSelectedPlayer(parseInt(playerIndex))
            setScreen('player')
          } else {
            setScreen('prerace')
          }
        } else {
          // No players assigned yet — show setup if creator, waiting if not
          setScreen(creatorHint ? 'setup' : 'prerace')
        }
      })
      .catch(() => setError('Failed to load game'))
  }, [slug, token, playerIndex])

  const handleSetupComplete = (_gameId: string, asn: Assignment[], p: Prizes) => {
    setAssignments(asn)
    setPrizes(p)
    setScreen('prerace')
  }

  // Override PlayerSetup's submit to post to /api/games/:slug/setup
  const handleGameSetup = async (gameId: string, asn: Assignment[], p: Prizes) => {
    handleSetupComplete(gameId, asn, p)
  }

  const handleStartRace = () => {
    if (!raceStarted) {
      fetch(`/api/games/${slug}/start`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` }
      })
      fetch('/api/race/start', { method: 'POST' })
      setRaceStarted(true)
    }
    setScreen('race')
  }

  const shareUrl = `${window.location.origin}/game/${slug}`

  if (screen === 'loading') {
    return (
      <div className="app" style={{ minHeight: '100vh', position: 'relative', overflow: 'hidden' }}>
        <BorgWarnerBackground />
        <header className="app-header" style={{ position: 'relative', zIndex: 1 }}>
          <div className="header-logo">🏎️</div>
          <div className="header-title"><h1>Race Day Pool</h1></div>
        </header>
        <main className="app-main" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', position: 'relative', zIndex: 1 }}>
          <div style={{ color: 'rgba(255,255,255,0.5)' }}>Loading game…</div>
        </main>
      </div>
    )
  }

  return (
    <div className="app" style={{ minHeight: '100vh', position: 'relative', overflow: 'hidden' }}>
      <BorgWarnerBackground />
      <header className="app-header" style={{ position: 'relative', zIndex: 1 }}>
        <div className="header-logo">🏎️</div>
        <div className="header-title">
          <h1>Race Day Pool</h1>
          <p style={{ fontSize: '0.72rem', opacity: 0.6 }}>{slug}</p>
        </div>
        <nav className="header-nav">
          {user && <button className="nav-btn" onClick={() => navigate('/dashboard')}>🎮 My Games</button>}
          {assignments.length > 0 && screen !== 'prerace' && (
            <button className="nav-btn" onClick={() => setScreen('prerace')}>📋 Roster</button>
          )}
          {raceStarted && screen !== 'race' && (
            <button className="nav-btn" onClick={() => setScreen('race')}>🔴 Live</button>
          )}
          <button
            className="nav-btn"
            style={{ fontSize: '0.72rem' }}
            onClick={() => { navigator.clipboard.writeText(shareUrl); alert('Link copied!') }}
          >
            📋 Share
          </button>
        </nav>
      </header>

      <main className="app-main" style={{ position: 'relative', zIndex: 1 }}>
        {error && <div className="alert alert-error">{error}</div>}

        {screen === 'setup' && (
          <PlayerSetup onSetupComplete={handleGameSetup} gameSlug={slug} token={token ?? undefined} />
        )}

        {screen === 'prerace' && assignments.length > 0 && (
          <PreRaceDashboard
            assignments={assignments}
            prizes={prizes}
            gameId={slug ?? ''}
            raceStarted={raceStarted}
            onStartRace={isCreator ? handleStartRace : undefined}
            onViewPlayer={(idx) => { setSelectedPlayer(idx); setScreen('player') }}
          />
        )}

        {screen === 'prerace' && assignments.length === 0 && (
          <div style={{ textAlign: 'center', padding: '4rem 1rem', color: 'rgba(255,255,255,0.45)' }}>
            <div style={{ fontSize: '2rem', marginBottom: '0.8rem' }}>⏳</div>
            <div>Waiting for the game creator to assign drivers…</div>
          </div>
        )}

        {screen === 'race' && assignments.length > 0 && (
          <RaceDayDashboard
            assignments={assignments}
            prizes={prizes}
            onRaceFinish={(lb) => { setFinalLeaderboard(lb); setScreen('results') }}
            onViewPlayer={(idx) => { setSelectedPlayer(idx); setScreen('player') }}
          />
        )}

        {screen === 'player' && assignments.length > 0 && (
          <PlayerPage
            playerIndex={selectedPlayer}
            assignments={assignments}
            prizes={prizes}
            onBack={() => setScreen(raceStarted ? 'race' : 'prerace')}
          />
        )}

        {screen === 'results' && (
          <Results
            assignments={assignments}
            prizes={prizes}
            finalLeaderboard={finalLeaderboard}
            onNewGame={() => navigate('/dashboard')}
          />
        )}
      </main>
    </div>
  )
}
